export interface PatientInfoTableConfig {
  fieldPriority: string[];
  excludedFields: string[];
  fieldLabels?: Record<string, string>;
}

export const patientInfoTableConfig: PatientInfoTableConfig = {
  fieldPriority: [
    'name',
    'date_of_birth',
    'age',
    'sex',
    'phone',
    'email',
    'address',
    'primary_pharmacy',
    'insurance',
    'primary_language',
    'emergency_contact',
    'id',
  ],
  excludedFields: ['origin_ids'],
  fieldLabels: {
    date_of_birth: 'date of birth',
    primary_pharmacy: 'primary pharmacy',
  },
};

function toPriorityIndex(priority: string[]): Map<string, number> {
  return new Map(priority.map((key, index) => [key, index]));
}

const PRIORITY_INDEX = toPriorityIndex(patientInfoTableConfig.fieldPriority);

export function getPatientInfoTableEntries(patientInfo: object): Array<[string, unknown]> {
  return Object.entries(patientInfo as Record<string, unknown>)
    .filter(([key]) => !patientInfoTableConfig.excludedFields.includes(key))
    .sort(([a], [b]) => {
      const aPriority = PRIORITY_INDEX.get(a);
      const bPriority = PRIORITY_INDEX.get(b);

      if (aPriority !== undefined && bPriority !== undefined) {
        return aPriority - bPriority;
      }
      if (aPriority !== undefined) return -1;
      if (bPriority !== undefined) return 1;
      return a.localeCompare(b);
    });
}

export function formatPatientInfoKey(key: string): string {
  const configuredLabel = patientInfoTableConfig.fieldLabels?.[key];
  if (configuredLabel) return configuredLabel;
  return key.replaceAll('_', ' ');
}

export function formatPatientInfoValue(value: unknown): string {
  if (value === null) return '—';
  if (value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) {
    return value.length > 0 ? value.map((v) => formatPatientInfoValue(v)).join(', ') : '—';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '—';
    }
  }
  return '—';
}
